/**
 * One integration (docs/systems.md "Systems view"): a process carrying data from one system to another. Where it reads
 * and writes (each system opens its page), its state, its runs over time as bars (failed ones red), the share of runs
 * without failures, how often it usually runs and when it counts as late, the data models it carries (each opens the
 * data search), its newest runs with the failed first (open in the run peek), and "Search this integration's data".
 * The range is in the URL, each change a history step; peeks follow the peek rules.
 */
import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import dayjs from 'dayjs';
import { Api } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { useEnv } from '../lib/envs.tsx';
import { useSearchParams } from '../lib/liveparams.ts';
import { rememberSearch } from '../lib/hooks.ts';
import { count, duration } from '../lib/format.ts';
import { cadenceLabel } from '../lib/integrations.ts';
import { integrationQuery, percent, rangeFrom, rangeWords, readRange, withRange } from '../lib/systemsview.ts';
import { stripSystem, systemOf } from '../lib/systems.ts';
import { Shell } from '../shell/Shell.tsx';
import { NounIcon, Surface, cx, noun } from '../design/primitives.tsx';
import { BackIcon, SearchIcon } from '../components/icons.tsx';
import { Peek, PeekLayout, usePeek } from '../peek/Peek.tsx';
import { useClock } from '../now/useLive.ts';
import { ActionLink, RangePills, RunBars, Section, StatusMark, SystemLink, NameToggle } from '../systems/parts.tsx';
import { useRangeLive } from '../systems/useRangeLive.ts';

const POLL_MS = 60_000;
const search = (q: string) => `/s?${new URLSearchParams({ q })}`;
const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

export function IntegrationPage() {
  const { process = '' } = useParams();
  const { tenant } = useAuth();
  const tid = tenant?.id ?? '';
  const navigate = useNavigate();
  const { link, current } = useEnv();
  const [params, setParams] = useSearchParams();
  const range = readRange(params);
  const peek = usePeek();
  const now = useClock(30_000);
  const time = useMemo(() => new URLSearchParams({ from: rangeFrom(range) }), [range]);

  const live = useRangeLive((r) => Api.integration(tid, process, new URLSearchParams({ from: rangeFrom(r) })), range, POLL_MS, [tid, process, current?.id]);
  const d = live.data;
  const shownRange = d?.range ?? range;
  const cadence = d ? cadenceLabel(d.cadenceMs) : null;
  const go = (q: string) => {
    if (!q) return;
    rememberSearch(q);
    void navigate(link(search(q)));
  };
  const own = systemOf(process);
  const sameDay = (t: number) => dayjs(t).isSame(dayjs(now), 'day');
  const at = (t: number) => (sameDay(t) ? dayjs(t).format('HH:mm') : dayjs(t).format('ddd D.M. HH:mm'));
  const late = !!d && d.status === 'quiet';

  return (
    <Shell search={{ value: '', onSubmit: go, size: 'compact' }}>
      <PeekLayout open={peek.stack.length > 0} peek={<Peek time={time} />}>
        <div className="sys-page" data-integration-page={process}>
          <Link to={link('/systems')} className="sys-crumb"><BackIcon size={14} />Systems</Link>

          <header className="sys-head mt-4">
            <div className="min-w-0 flex flex-col gap-2.5">
              <h1 className="m-0 text-[34px] leading-[40px] font-semibold tracking-tight [overflow-wrap:anywhere]">{stripSystem(process) || process}</h1>
              <div className="flex items-center gap-x-4 gap-y-2 flex-wrap text-small">
                {d?.integrations.map((i) => (
                  <span key={`${i.from}>${i.to}`} className="inline-flex items-center gap-2 min-w-0">
                    {i.from && <SystemLink code={i.from} size={24} />}
                    {i.from && i.to && i.from !== i.to && <span className="text-n7" aria-label="to">→</span>}
                    {i.to && i.to !== i.from && <SystemLink code={i.to} size={24} />}
                    {(!i.from || !i.to || i.from === i.to) && <span className="text-micro text-n7">{i.from && i.to ? 'within the system' : i.from ? 'reads only (nothing written is logged)' : 'writes only (nothing read is logged)'}</span>}
                  </span>
                ))}
                {d && <StatusMark status={d.status} />}
              </div>
              <p className="m-0 font-mono text-micro text-n7 truncate">{process}{own && !d?.systems.includes(own) ? ` · named for ${own}` : ''}</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <NameToggle />
              <RangePills value={range} onChange={(r) => setParams((p) => withRange(p, r))} />
              <ActionLink to={link(search(integrationQuery(process)))}><SearchIcon size={15} />Search this integration’s data</ActionLink>
            </div>
          </header>

          {live.error && !d && <p className="m-0 mt-8 text-body text-fail">{live.error}</p>}
          {!d && !live.error && <div className="mt-8 flex flex-col gap-4" aria-busy="true"><span className="mv-shimmer rounded-card h-24" /><span className="mv-shimmer rounded-card h-40" /></div>}
          {d && d.integrations.length === 0 && d.runs === 0 && (
            <p className="m-0 mt-8 text-body text-n8">No runs of this integration in the last 7 days. It may have stopped, or its documents carry no run (<span className="font-mono">executionId</span>).</p>
          )}

          {d && (d.integrations.length > 0 || d.runs > 0) && (
            <>
              {(d.status === 'failing' || late) && (
                <div className={cx('mt-6 rounded-card px-4 py-3 text-body flex items-center gap-3', d.status === 'failing' ? 'bg-fail-soft text-n10' : 'bg-warn-soft text-n10')} data-integration-alert="">
                  <i className={cx('size-2 rounded-full shrink-0', d.status === 'failing' ? 'bg-fail' : 'bg-warn')} aria-hidden="true" />
                  {d.status === 'failing'
                    ? <span>The newest run failed{d.lastRun ? ` (${at(d.lastRun)})` : ''}. {d.failedRuns > 0 ? `${count(d.failedRuns)} ${d.failedRuns === 1 ? 'run' : 'runs'} with failures in the ${rangeWords(shownRange)}.` : ''}</span>
                    : <span>Overdue: silent for {d.lastRun ? duration(Math.max(60_000, now - d.lastRun)) : 'a long time'}{cadence ? `, usually runs ${cadence}` : ''}.</span>}
                </div>
              )}

              <div className="sys-facts mt-6" data-integration-facts="">
                <Fact value={count(d.runs)} label={`runs · ${rangeWords(shownRange)}`} />
                <Fact value={percent(d.successRate)} label="without failures" tone={d.failedRuns > 0 ? 'fail' : undefined} />
                <Fact value={count(d.failedRuns)} label="runs with failures" tone={d.failedRuns > 0 ? 'fail' : undefined} />
                <Fact value={count(d.documents)} label="documents" />
                <Fact value={cadence ?? '—'} label="usually runs" />
                <Fact value={d.lastRun ? `${at(d.lastRun)}` : '—'} label={d.lastRun ? `last run · ${duration(Math.max(1000, now - d.lastRun))} ago` : 'last run'} tone={late ? 'warn' : undefined} />
              </div>

              <Section title="Runs over time" fact={rangeWords(shownRange)} className="mt-10" id="int-runs">
                <Surface className="p-5">
                  <RunBars bars={d.timeline} height={96} />
                </Surface>
                <p className="m-0 text-small text-n8" data-integration-cadence="">
                  {d.nextDue ? (
                    <>
                      {d.nextDue > now ? <>Next run due about {at(d.nextDue)}</> : <>A run was due about {at(d.nextDue)}</>}
                      {d.lateAt ? <> · counts as late after {at(d.lateAt)} (three times its usual gap)</> : <> · too few runs to tell when it is late</>}
                    </>
                  ) : 'Too few runs to tell how often it runs.'}
                  {d.truncated && <span className="text-n7"> · Only the newest runs were listed; the hatched bars are older.</span>}
                </p>
              </Section>

              <div className="sys-body two mt-10">
                <Section title="Latest runs" fact={d.failedRuns > 0 ? 'failed first' : rangeWords(shownRange)} id="int-latest">
                  {d.latest.length ? (
                    <Surface className="p-1.5">
                      <div className="sys-rows">
                        {d.latest.map((r) => (
                          <button key={r.executionId} type="button" data-run={r.executionId} onClick={() => peek.open({ kind: 'run', executionId: r.executionId })}
                            className="grid grid-cols-[minmax(84px,auto)_minmax(0,1fr)_auto] gap-3 items-center w-full px-3 py-2.5 rounded-chip bg-transparent border-0 cursor-pointer text-left text-small text-n10 hover:bg-n3 focus-visible:outline-2 focus-visible:outline-accent">
                            <span className="font-mono text-micro text-n7 whitespace-nowrap">{at(r.first)}</span>
                            <span className="flex items-center gap-2 min-w-0">
                              <NounIcon kind="run" size={16} />
                              <span className="truncate text-n9">{r.classes.map((c) => noun(c)).join(' → ') || 'run'}</span>
                              <span className="text-micro text-n7 whitespace-nowrap">{count(r.total)} docs{r.last > r.first ? ` · ${duration(r.last - r.first)}` : ''}</span>
                            </span>
                            {r.failed > 0
                              ? <span className="inline-flex items-center gap-1.5 text-fail whitespace-nowrap"><i className="size-2 rounded-full bg-fail" aria-hidden="true" />{count(r.failed)} failed</span>
                              : <span className="inline-flex items-center gap-1.5 text-n8 whitespace-nowrap"><i className="size-2 rounded-full bg-ok" aria-hidden="true" />done</span>}
                          </button>
                        ))}
                      </div>
                    </Surface>
                  ) : <p className="m-0 text-small text-n7">No runs in the {rangeWords(shownRange)}.</p>}
                </Section>

                <Section title="Carries" fact={count(d.dataModels.length)} id="int-models">
                  {d.dataModels.length ? (
                    <Surface className="p-1.5">
                      <div className="sys-rows">
                        {d.dataModels.map((c) => (
                          <Link key={c} to={link(search(`@class=${c} ${integrationQuery(process)}`))} data-model={c}
                            className="grid grid-cols-[18px_minmax(0,1fr)] gap-3 items-center px-3 py-2.5 rounded-chip no-underline text-small text-n10 hover:bg-n3">
                            <NounIcon kind="document" size={16} />
                            <span className="flex flex-col min-w-0"><span className="font-medium truncate">{cap(noun(c, 2))}</span><span className="font-mono text-micro text-n7 truncate">{c}</span></span>
                          </Link>
                        ))}
                      </div>
                    </Surface>
                  ) : <p className="m-0 text-small text-n7">No data models seen.</p>}
                  <p className="m-0 text-micro text-n7">A data model opens the data search for its documents of this integration.</p>
                </Section>
              </div>
              {d.partial && <p className="m-0 mt-6 text-micro text-warn">Some vaults did not answer; what is shown may be incomplete.</p>}
            </>
          )}
        </div>
      </PeekLayout>
    </Shell>
  );
}

function Fact({ value, label, tone }: { value: string; label: string; tone?: 'fail' | 'warn' }) {
  return <div className={cx('sys-fact', tone)}><b>{value}</b><span>{label}</span></div>;
}
